export const COLUMN_MIN_WIDTHS = Object.freeze({
	spacer: 30,
	compact: 64,
	id: 100,
	action: 120,
	number: 120,
	status: 140,
	text: 170,
	datetime: 150,
	person: 100,
	media: 340
})

const COLUMN_PRESETS = {
	id: 'id', partId: 'id', orderId: 'id', externalOrderId: 'id', accountingCode: 'id',
	sku: 'id', ean: 'id', groupedSale: 'id',
	parts: 'media', part: 'media',
	createdAt: 'datetime', updatedAt: 'datetime', paymentCompletedAt: 'datetime', dateCompleted: 'datetime',
	orderCreatedAt: 'datetime', orderPaidAt: 'datetime',
	status: 'status', state: 'status', demand: 'status',
	price: 'number', totalPrice: 'number', totalWithVat: 'number', cost: 'number', quantity: 'number',
	discount: 'number', vatAmount: 'number', vatPercentage: 'number', sellPrice: 'number',
	suggestedPrice: 'number', suggestedPriceWithVat: 'number', profit: 'number', profitWithVat: 'number',
	margin: 'number',
	PaymentCompleted: 'compact', paymentCompleted: 'compact', orderPaymentCompleted: 'compact',
	handover: 'compact', tools: 'compact', isSaleAllowed: 'compact',
	inputUser: 'person', orderInputUser: 'person', user: 'person'
}

export function getColumnMinWidth (column = {}) {
	const override = Number(column.minWidth)
	if (Number.isFinite(override) && override > 0) return Math.max(30, override)
	const preset = column.widthPreset || (column.children?.some(child => child.sortKey === 'photo')
		? 'media'
		: COLUMN_PRESETS[column.sortKey] || 'text')
	return COLUMN_MIN_WIDTHS[preset] || COLUMN_MIN_WIDTHS.text
}

export function getColumnResizedWidth (column = {}) {
	const width = Number(column.resizedWidth)
	return Number.isFinite(width) && width > 0 ? width : null
}

export function mergeColumnSizing (columns = [], source = []) {
	if (!Array.isArray(columns)) return []
	const key = column => column?.sortKey || column?.key || column?.tag || column?.name
	const definitions = new Map(source.map(column => [key(column), column]))
	return columns.map(column => {
		const match = key(column) && definitions.get(key(column))
		if (!match) return column
		return { ...column, widthPreset: match.widthPreset, minWidth: match.minWidth }
	})
}

export function getColumnSpan (column = {}) {
	const span = Number(column.span)
	return Number.isFinite(span) && span > 0 ? span : 1
}

function distributeColumnWidths (columns, indexes, amount, lowerBounds, widths) {
	const spans = columns.map(getColumnSpan)
	let remaining = Math.max(0, amount)
	let pending = indexes.slice()
	let scale = 0
	while (pending.length) {
		const totalSpan = pending.reduce((sum, index) => sum + spans[index], 0)
		scale = remaining / totalSpan
		const constrained = pending.filter(index => spans[index] * scale < lowerBounds[index])
		if (!constrained.length) {
			pending.forEach(index => { widths[index] = spans[index] * scale })
			break
		}
		constrained.forEach(index => {
			widths[index] = lowerBounds[index]
			remaining -= lowerBounds[index]
		})
		pending = pending.filter(index => !constrained.includes(index))
	}
	return scale
}

export function allocateColumnWidths (columns = [], availableWidth = 0) {
	const minimums = columns.map(getColumnMinWidth)
	const pinnedWidths = columns.map((column, index) => {
		const width = getColumnResizedWidth(column)
		return width === null ? null : Math.max(minimums[index], width)
	})
	const pinnedIndexes = columns.map((_, index) => index).filter(index => pinnedWidths[index] !== null)
	const flexibleIndexes = columns.map((_, index) => index).filter(index => pinnedWidths[index] === null)
	const pinnedTotal = pinnedIndexes.reduce((sum, index) => sum + pinnedWidths[index], 0)
	const flexibleMinimumTotal = flexibleIndexes.reduce((sum, index) => sum + minimums[index], 0)
	const minimumTotal = minimums.reduce((sum, width) => sum + width, 0)
	const budget = Math.max(
		pinnedTotal + flexibleMinimumTotal,
		Number.isFinite(availableWidth) ? availableWidth : 0
	)
	const widths = minimums.slice()
	pinnedIndexes.forEach(index => { widths[index] = pinnedWidths[index] })
	let scale = 0
	const flexibleBudget = budget - pinnedTotal
	if (flexibleIndexes.length && flexibleBudget > flexibleMinimumTotal) {
		scale = distributeColumnWidths(
			columns,
			flexibleIndexes,
			flexibleBudget,
			minimums,
			widths
		)
	}
	return { widths, budget, minimumTotal, scale, pinnedTotal }
}

export function resizeColumnSizing (columns, index, targetWidth, availableWidth) {
	const column = columns[index]
	const originalSpan = getColumnSpan(column)
	const originalResizedWidth = getColumnResizedWidth(column)
	if (!column || !Number.isFinite(targetWidth)) {
		return { span: originalSpan, resizedWidth: originalResizedWidth }
	}
	const layout = allocateColumnWidths(columns, availableWidth)
	const minimum = getColumnMinWidth(column)
	const target = Math.max(minimum, targetWidth)
	if (Math.abs(target - layout.widths[index]) < 0.5) {
		return { span: originalSpan, resizedWidth: originalResizedWidth }
	}
	if (columns.length < 2) {
		return { span: originalSpan, resizedWidth: target }
	}

	const siblings = columns.filter((_, columnIndex) => columnIndex !== index)
	const pinnedSiblingTotal = siblings.reduce((sum, sibling) => {
		const siblingMinimum = getColumnMinWidth(sibling)
		const resizedWidth = getColumnResizedWidth(sibling)
		return sum + (resizedWidth === null ? 0 : Math.max(siblingMinimum, resizedWidth))
	}, 0)
	const flexibleSiblings = siblings.filter(sibling => getColumnResizedWidth(sibling) === null)
	const flexibleSiblingMinimumTotal = flexibleSiblings.reduce((sum, sibling) => sum + getColumnMinWidth(sibling), 0)
	const baseBudget = Math.max(
		layout.minimumTotal,
		Number.isFinite(availableWidth) ? availableWidth : 0
	)
	const proportionalMaximum = baseBudget - pinnedSiblingTotal - flexibleSiblingMinimumTotal
	const canUseSpan = flexibleSiblings.length > 0 && target <= proportionalMaximum + 0.5
	const siblingCurrentTotal = layout.widths.reduce((sum, width, columnIndex) => {
		return columnIndex === index ? sum : sum + width
	}, 0)
	const desiredBudget = canUseSpan ? baseBudget : target + siblingCurrentTotal
	const { scale } = allocateColumnWidths(siblings, Math.max(0, desiredBudget - target))
	return {
		span: scale > 0 ? Number((target / scale).toPrecision(12)) : originalSpan,
		resizedWidth: canUseSpan ? null : target
	}
}

export function serializeColumnSizing (column = {}) {
	const span = getColumnSpan(column)
	const resizedWidth = getColumnResizedWidth(column)
	return resizedWidth === null ? span : { span, resizedWidth }
}

export function applySavedColumnSizing (column = {}, saved) {
	const next = { ...column }
	if (saved && typeof saved === 'object') {
		const span = Number(saved.span)
		if (Number.isFinite(span) && span > 0) next.span = span
		const resizedWidth = getColumnResizedWidth(saved)
		if (resizedWidth === null) {
			delete next.resizedWidth
		} else {
			next.resizedWidth = resizedWidth
		}
		return next
	}
	const span = Number(saved)
	if (!Number.isFinite(span) || span <= 0) return column
	next.span = span
	delete next.resizedWidth
	return next
}

export function normalizeColumns (columns = [], source = []) {
	const knownSortKeys = new Set(source.map(column => column.sortKey))

	return columns.reduce((acc, column) => {
		if (!column || !knownSortKeys.has(column.sortKey)) {
			return acc
		}

		const match = source.find(c => c.sortKey === column.sortKey)
		const normalized = { ...match, ...column, name: match.name, tag: match.tag, widthPreset: match.widthPreset, minWidth: match.minWidth }

		if (match.children && Array.isArray(column.children)) {
			normalized.children = column.children.reduce((childAcc, child) => {
				const childMatch = match.children.find(c => c.sortKey === child.sortKey)
				if (childMatch) {
					childAcc.push({ ...childMatch })
				}
				return childAcc
			}, [])

			if (!normalized.children.length) {
				normalized.children = match.children.map(child => ({ ...child }))
			}
		}

		acc.push(normalized)
		return acc
	}, [])
}
